import React, { useMemo, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, IconButton, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import { THEME_COLOR, buttonStyles } from '../../../theme/formStyles';
import { engineerName, fmtDate, fmtDuration, isOnDay, isOpenVisit, monthWeeks, packWeek, subTaskStatusStyle, todayYmd, visitMinutes } from '../projectSchedule.utils';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_LANES = 3;
const toggleSx = {
    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 500, px: 1.75, py: 0.6, color: '#6b7785' },
    '& .MuiToggleButton-root.Mui-selected': { bgcolor: '#e6f4f2', color: THEME_COLOR, '&:hover': { bgcolor: '#d7eeeb' } },
};

const isLiveTask = (task, visits, today) => visits.some(v => v.sub_task_id === task.id && v.visit_date === today && isOpenVisit(v));

function ListRow({ task, visits, onOpen }) {
    const today = todayYmd();
    const minutes = visits.filter(v => v.sub_task_id === task.id).reduce((s, v) => s + visitMinutes(v), 0);
    const live = isLiveTask(task, visits, today);
    const look = subTaskStatusStyle(task.status);
    return (
        <Box onClick={() => onOpen(task)} sx={{ display: 'flex', gap: 1.5, alignItems: 'center', p: '11px 12px', border: '1px solid #e5e9ee', borderRadius: 3, mb: 1, cursor: 'pointer', bgcolor: '#fff', '&:hover': { borderColor: THEME_COLOR } }}>
            <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: 1, bgcolor: look.color }} />
            <Box sx={{ width: { xs: 60, sm: 82 }, flex: 'none' }}>
                <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>{fmtDate(task.start_date, 'D MMM')}</Typography>
                <Typography sx={{ fontSize: '12px', color: '#6b7785' }}>{task.start_date !== task.end_date ? `to ${fmtDate(task.end_date, 'D MMM')}` : fmtDate(task.start_date, 'dddd')}</Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500 }}>{task.sub_task_name}</Typography>
                <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }}>
                    {task.spoc ? engineerName(task) : 'Engineer to be assigned'}{minutes > 0 && ` · ${fmtDuration(minutes)} on site`}
                </Typography>
            </Box>
            {live
                ? <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#16a34a' }}>● On site</Typography>
                : <Chip size="small" label={task.status} sx={{ fontWeight: 700, fontSize: '11px', height: 22, ...look }} />}
        </Box>
    );
}

// Client calendar: a month grid with each visit drawn as one bar across its days, or a list view.
// Chips filter by task so a client can look at, say, only the installation work.
export default function ClientCalendar({ tasks, visits, cursor, onCursorChange, onOpen }) {
    const isPhone = useMediaQuery('(max-width:700px)');
    const [view, setView] = useState('month');
    const [taskFilter, setTaskFilter] = useState('all');
    const [pickedDay, setPickedDay] = useState(todayYmd());
    const today = todayYmd();

    const taskNames = useMemo(() => {
        const counts = new Map();
        tasks.forEach(t => counts.set(t.task_name || 'Other', (counts.get(t.task_name || 'Other') || 0) + 1));
        return Array.from(counts.entries());
    }, [tasks]);
    const shown = taskFilter === 'all' ? tasks : tasks.filter(t => (t.task_name || 'Other') === taskFilter);
    const weeks = monthWeeks(cursor.year, cursor.month);
    const shift = (delta) => {
        const d = dayjs().year(cursor.year).month(cursor.month).date(1).add(delta, 'month');
        onCursorChange({ year: d.year(), month: d.month() });
    };

    const toolbar = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 1.5 }}>
            <Button variant="outlined" size="small" sx={buttonStyles.outlined} onClick={() => onCursorChange({ year: dayjs().year(), month: dayjs().month() })}>Today</Button>
            <IconButton size="small" onClick={() => shift(-1)} aria-label="Previous month"><ChevronLeftIcon /></IconButton>
            <IconButton size="small" onClick={() => shift(1)} aria-label="Next month"><ChevronRightIcon /></IconButton>
            <Typography sx={{ fontSize: '17px', fontWeight: 500, minWidth: 130 }}>{dayjs().year(cursor.year).month(cursor.month).format('MMMM YYYY')}</Typography>
            <ToggleButtonGroup exclusive size="small" value={view} onChange={(e, v) => v && setView(v)} sx={{ ...toggleSx, ml: { sm: 'auto' } }}>
                <ToggleButton value="month">Month</ToggleButton>
                <ToggleButton value="list">List</ToggleButton>
            </ToggleButtonGroup>
        </Box>
    );

    const filters = taskNames.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1.25, scrollbarWidth: 'none' }}>
            {[['all', tasks.length], ...taskNames].map(([name, count]) => {
                const on = taskFilter === name;
                return (
                    <Chip key={name} clickable onClick={() => setTaskFilter(name)} label={`${name === 'all' ? 'All' : name} ${count}`}
                        sx={{ flex: 'none', fontWeight: 500, bgcolor: on ? '#1f2933' : '#fff', color: on ? '#fff' : '#6b7785', border: '1px solid', borderColor: on ? '#1f2933' : '#e5e9ee', '&:hover': { bgcolor: on ? '#1f2933' : '#f7fafc' } }} />
                );
            })}
        </Box>
    );

    if (view === 'list') {
        const sorted = [...shown].sort((a, b) => a.start_date.localeCompare(b.start_date));
        const now = sorted.filter(t => isOnDay(t, today));
        const next = sorted.filter(t => t.start_date > today);
        const done = sorted.filter(t => t.end_date < today).reverse();
        const groupTitle = (text) => <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#6b7785', textTransform: 'uppercase', letterSpacing: '.05em', mt: 2, mb: 1 }}>{text}</Typography>;
        return (
            <Box>
                {toolbar}{filters}
                {now.length > 0 && <>{groupTitle('Today')}{now.map(t => <ListRow key={t.id} task={t} visits={visits} onOpen={onOpen} />)}</>}
                {next.length > 0 && <>{groupTitle('Coming up')}{next.map(t => <ListRow key={t.id} task={t} visits={visits} onOpen={onOpen} />)}</>}
                {done.length > 0 && (
                    <Accordion disableGutters sx={{ mt: 2, border: '1px solid #e5e9ee', boxShadow: 'none', borderRadius: '12px !important', '&:before': { display: 'none' } }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography sx={{ fontWeight: 500, fontSize: '13.5px' }}>Completed <span style={{ color: '#6b7785', fontWeight: 400 }}>· {done.length}</span></Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>{done.map(t => <ListRow key={t.id} task={t} visits={visits} onOpen={onOpen} />)}</AccordionDetails>
                    </Accordion>
                )}
                {!sorted.length && <Typography sx={{ textAlign: 'center', color: '#6b7785', fontSize: '13px', py: 3 }}>No visits scheduled.</Typography>}
            </Box>
        );
    }

    const pickedInMonth = weeks.some(w => w.some(c => c.inMonth && c.ymd === pickedDay));
    const pickedTasks = shown.filter(t => isOnDay(t, pickedDay));

    return (
        <Box>
            {toolbar}{filters}
            <Box sx={{ border: '1px solid #e5e9ee', borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', bgcolor: '#fafbfc', borderBottom: '1px solid #e5e9ee' }}>
                    {DAY_NAMES.map(d => <Typography key={d} sx={{ textAlign: 'center', py: 1, fontSize: '12.5px', fontWeight: 500, color: '#6b7785' }}>{isPhone ? d[0] : d}</Typography>)}
                </Box>
                {weeks.map((week, wi) => {
                    const { placed, more } = packWeek(shown, week, MAX_LANES);
                    return (
                        <Box key={wi} sx={{ position: 'relative', minHeight: isPhone ? 66 : 122, borderBottom: wi < weeks.length - 1 ? '1px solid #e5e9ee' : 'none' }}>
                            {/* Day cells */}
                            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))' }}>
                                {week.map((cell, ci) => {
                                    const isToday = cell.ymd === today;
                                    const picked = isPhone && cell.ymd === pickedDay;
                                    return (
                                        <Box key={cell.ymd} onClick={() => setPickedDay(cell.ymd)}
                                            sx={{ borderLeft: ci ? '1px solid #e5e9ee' : 'none', p: isPhone ? '4px 0' : '6px 8px', textAlign: isPhone ? 'center' : 'left', cursor: 'pointer',
                                                bgcolor: !cell.inMonth ? '#fafbfc' : isToday ? '#f3fbf9' : '#fff' }}>
                                            <Typography component="span" sx={{
                                                fontSize: '13px', fontWeight: 500, color: cell.inMonth ? '#1f2933' : '#b4bcc6',
                                                ...((isToday || picked) && { bgcolor: isToday ? THEME_COLOR : '#1f2933', color: '#fff', borderRadius: 99, px: 0.9 }),
                                            }}>{cell.day}</Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                            {/* Visit bars */}
                            <Box sx={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gridAutoRows: isPhone ? '18px' : '24px', rowGap: '4px', pt: isPhone ? '28px' : '32px', pb: 1, pointerEvents: 'none' }}>
                                {placed.map(({ task, from, to, lane, clipLeft, clipRight }) => {
                                    const look = subTaskStatusStyle(task.status);
                                    const live = isLiveTask(task, visits, today);
                                    return (
                                        <Box key={`${task.id}-${wi}`} onClick={() => onOpen(task)} title={`${task.sub_task_name} · ${task.spoc ? engineerName(task) : 'Engineer to be assigned'}`}
                                            sx={{
                                                gridRow: lane + 1, gridColumn: `${from + 1} / ${to + 2}`, pointerEvents: 'auto', cursor: 'pointer', minWidth: 0,
                                                display: 'flex', alignItems: 'center', gap: 0.75, px: isPhone ? 0.5 : 1, mx: isPhone ? '1px' : '4px',
                                                ...(clipLeft && { ml: 0 }), ...(clipRight && { mr: 0 }),
                                                borderRadius: '6px', ...(clipLeft && { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }), ...(clipRight && { borderTopRightRadius: 0, borderBottomRightRadius: 0 }),
                                                borderLeft: `3px ${clipLeft ? 'dashed' : 'solid'} ${look.color}`, bgcolor: look.bgcolor, color: look.color,
                                                opacity: task.status === 'Completed' ? 0.65 : 1, fontSize: isPhone ? '9.5px' : '12.5px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden',
                                                '&:hover': { filter: 'brightness(.96)', boxShadow: '0 1px 4px rgba(0,0,0,.12)' },
                                            }}>
                                            {live && !isPhone && <Box component="i" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#16a34a', flex: 'none' }} />}
                                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.sub_task_name}</Box>
                                        </Box>
                                    );
                                })}
                                {more.map((n, c) => (n > 0 && (
                                    <Typography key={`more-${c}`} onClick={() => setView('list')}
                                        sx={{ gridRow: MAX_LANES + 1, gridColumn: c + 1, pointerEvents: 'auto', cursor: 'pointer', fontSize: isPhone ? '9.5px' : '12px', color: THEME_COLOR, fontWeight: 500, pl: 1 }}>
                                        +{n} more
                                    </Typography>
                                )))}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            {/* Phones: the bars are small, so list the tapped day's visits underneath */}
            {isPhone && (
                <Box sx={{ mt: 1.5 }}>
                    {pickedInMonth ? (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>{fmtDate(pickedDay, 'dddd D MMMM')}</Typography>
                                <Typography sx={{ fontSize: '12.5px', color: '#6b7785' }}>{pickedTasks.length} visit{pickedTasks.length === 1 ? '' : 's'}</Typography>
                            </Box>
                            {pickedTasks.map(t => <ListRow key={t.id} task={t} visits={visits} onOpen={onOpen} />)}
                            {!pickedTasks.length && <Typography sx={{ textAlign: 'center', color: '#6b7785', fontSize: '13px', py: 1.5 }}>No visits on this day.</Typography>}
                        </>
                    ) : <Typography sx={{ textAlign: 'center', color: '#6b7785', fontSize: '13px', py: 1.5 }}>Tap a day to see its visits.</Typography>}
                </Box>
            )}
        </Box>
    );
}
